import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CellRef } from "../../../interfaces/Rule";
import { CoObjectItem } from "../../../interfaces/CoObject";
import { FilterBar } from "../../../generic/filterBar/FilterBar";
import { EditableCellPill } from "../../../generic/EditableCellPill";
import { ObjectListItemRow } from "../../../coobjects/object-list-panel/ObjectListItemRow";
import { PILL_BASE_STYLE } from "../../../generic/TrackedChangeOperationTypePill";
import { formatMessage, formatMessageDefault } from "../../../../utils/helpers/LanguageHelper";
import {
  getSelectedTableCells,
  scrollTableIntoView,
  getTableCellValues,
  selectContentControlById,
} from "../../../../office/ContentControl";
import { Link } from "@fluentui/react";
import {
  CAPTION_TT_SELECT_TABLE_TO_START,
  CAPTION_TT_SELECT_CELLS_AND_SUBTOTAL,
  CAPTION_TT_CELLS_TO_SUM,
  CAPTION_TT_SUBTOTALS,
  CAPTION_TT_SELECT_SUBTOTAL,
  CAPTION_TT_SELECT_CELL,
  CAPTION_TT_NO_TABLES,
} from "../validationCaptions";
import { formatTableTotalsCellAddress, formatTableTotalsCellLabel } from "../../../../utils/helpers/TableTotalsHelper";

type SelectedCell = { row: number; column: number; value: string };

export interface TableTotalsTableEntry {
  item: CoObjectItem;
  id: string;
  contentControlId: number;
  name: string;
}

export interface TableTotalsRuleEditorProps {
  tables: TableTotalsTableEntry[];
  tableId?: string;
  addends: CellRef[];
  subtotal?: CellRef;
  onBindTable: (table: TableTotalsTableEntry) => void;
  onAddAddends: (cells: CellRef[]) => void;
  onRemoveAddend: (cell: CellRef) => void;
  onSetSubtotal: (cell: CellRef) => void;
  onClearSubtotal: () => void;
  onReplaceAddend: (target: CellRef, newCell: CellRef) => void;
}

/**
 * Discriminated union for the cell-selection arm mode.
 * The handler is always registered once when a table is bound; only the mode
 * changes to direct what each selection event does.
 */
type ArmMode =
  | { kind: "addend" }
  | { kind: "subtotal" }
  | { kind: "replaceAddend"; target: CellRef }
  | { kind: "replaceSubtotal" };

/** Map key for a {row, column} cell reference. */
const toCellKey = (row: number, column: number): string => `${row},${column}`;

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ text: string }> = ({ text }) => (
  <div style={{ fontSize: 14, fontWeight: "bold", color: "var(--color-blue)" }}>{text}</div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export const TableTotalsRuleEditor: FunctionComponent<TableTotalsRuleEditorProps> = ({
  tables,
  tableId,
  addends,
  subtotal,
  onBindTable,
  onAddAddends,
  onRemoveAddend,
  onSetSubtotal,
  onClearSubtotal,
  onReplaceAddend,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [valueMap, setValueMap] = useState<Map<string, string>>(new Map());
  const [armMode, setArmMode] = useState<ArmMode>({ kind: "addend" });

  // Stable refs — read inside the async selection handler to avoid stale closures.
  const armModeRef = useRef<ArmMode>({ kind: "addend" });
  const handlerFnRef = useRef<(() => Promise<void>) | null>(null);
  const handlerActiveRef = useRef(false);
  const addendsRef = useRef<CellRef[]>(addends);
  const boundTableRef = useRef<TableTotalsTableEntry | undefined>(undefined);

  // Callback refs so the handler always calls the latest prop functions.
  const onAddAddends_ref = useRef(onAddAddends);
  const onSetSubtotal_ref = useRef(onSetSubtotal);
  const onReplaceAddend_ref = useRef(onReplaceAddend);

  const setArm = useCallback((mode: ArmMode): void => {
    armModeRef.current = mode;
    setArmMode(mode);
  }, []);

  // ── Derived ─────────────────────────────────────────────────────────────────

  const boundTable = useMemo(() => (tableId ? tables.find((t) => t.id === tableId) : undefined), [tables, tableId]);

  // Is tableId set but not found in tables? (deleted/missing table in edit mode)
  const isMissingTable = !!tableId && !boundTable;

  // ── Ref sync effects ────────────────────────────────────────────────────────

  useEffect(() => {
    addendsRef.current = addends;
  }, [addends]);

  useEffect(() => {
    boundTableRef.current = boundTable;
  }, [boundTable]);

  useEffect(() => {
    onAddAddends_ref.current = onAddAddends;
  }, [onAddAddends]);

  useEffect(() => {
    onSetSubtotal_ref.current = onSetSubtotal;
  }, [onSetSubtotal]);

  useEffect(() => {
    onReplaceAddend_ref.current = onReplaceAddend;
  }, [onReplaceAddend]);

  // ── Filtered table list ──────────────────────────────────────────────────────

  const filteredTables = useMemo(() => {
    if (!searchQuery) return tables;
    const q = searchQuery.toLowerCase();
    return tables.filter((t) => t.name.toLowerCase().includes(q));
  }, [tables, searchQuery]);

  // ── Listener management ──────────────────────────────────────────────────────

  const removeListener = useCallback((): void => {
    if (!handlerActiveRef.current || !handlerFnRef.current) return;
    Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
      handler: handlerFnRef.current,
    });
    handlerActiveRef.current = false;
    handlerFnRef.current = null;
  }, []);

  // ── Selection handler (all deps via refs — stable identity) ─────────────────

  /** Multi-cell addend capture: dedupe against current addends and cache values. */
  const applyAddendSelection = useCallback((cells: SelectedCell[]): void => {
    const currentAddends = addendsRef.current;
    const newCells: CellRef[] = [];
    const valueEntries: Array<[string, string]> = [];

    for (const cell of cells) {
      valueEntries.push([toCellKey(cell.row, cell.column), cell.value]);
      const isDup = currentAddends.some((a) => a.row === cell.row && a.column === cell.column);
      if (!isDup) {
        newCells.push({ row: cell.row, column: cell.column });
      }
    }

    setValueMap((prev) => {
      const next = new Map(prev);
      for (const [k, v] of valueEntries) next.set(k, v);
      return next;
    });

    if (newCells.length > 0) {
      onAddAddends_ref.current(newCells);
    }
  }, []);

  /** Single-cell capture (subtotal/replace): cache the value, invoke the callback, revert to addend. */
  const applySingleCellAndRevert = useCallback(
    (cell: SelectedCell, invoke: (target: CellRef) => void): void => {
      setValueMap((prev) => {
        const next = new Map(prev);
        next.set(toCellKey(cell.row, cell.column), cell.value);
        return next;
      });
      invoke({ row: cell.row, column: cell.column });
      // Revert to addend — listener stays active (INV-REVERT)
      setArm({ kind: "addend" });
    },
    [setArm]
  );

  const handleSelectionChanged = useCallback(async (): Promise<void> => {
    const mode = armModeRef.current;
    const bt = boundTableRef.current;
    if (!bt) return;

    try {
      const result = await getSelectedTableCells();
      if (!result) return; // null → selection not in any Tagetik CC
      if (result.contentControlId !== bt.contentControlId) return; // INV2
      if (result.selectionKind === "wholeContentControl") return;
      if (mode.kind !== "addend" && result.selectionKind === "cellRange") return;

      if (mode.kind === "addend") {
        applyAddendSelection(result.cells);
        return;
      }

      const cell = result.cells[0];
      if (!cell) return;

      if (mode.kind === "replaceAddend") {
        const { target } = mode;
        applySingleCellAndRevert(cell, (newCell) => onReplaceAddend_ref.current(target, newCell));
      } else {
        // subtotal & replaceSubtotal both set the subtotal, then revert to addend
        applySingleCellAndRevert(cell, (newCell) => onSetSubtotal_ref.current(newCell));
      }
    } catch (err) {
      console.error("[TableTotalsRuleEditor] selection handler error:", err);
    }
  }, [applyAddendSelection, applySingleCellAndRevert]);

  // ── Auto-arm listener once when table becomes bound (fix 6) ─────────────────
  // Registers the DocumentSelectionChanged handler exactly ONCE per bound table.
  // The scroll itself lives INSIDE the success callback so the handler is registered
  // before the programmatic selection fires; collapsed-start scroll selections are
  // non-cell selections and are ignored by the handler's null-result guard.
  useEffect(() => {
    removeListener();
    // Reset arm mode on every tableId change (also covers rebind and unmount)
    setArm({ kind: "addend" });

    if (!boundTable) {
      // No table bound (unbound state) or isMissingTable → do not register
      return;
    }

    handlerFnRef.current = handleSelectionChanged;
    // Capture contentControlId in a const so the callback closure is stable
    const { contentControlId } = boundTable;
    Office.context.document.addHandlerAsync(
      Office.EventType.DocumentSelectionChanged,
      handleSelectionChanged,
      (result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          handlerActiveRef.current = true;
          // Scroll INSIDE success callback — handler is registered before the
          // programmatic collapsed-start selection fires.
          void scrollTableIntoView(contentControlId);
        } else {
          // Registration failed — roll back handler ref
          handlerFnRef.current = null;
        }
      }
    );

    return () => {
      removeListener();
    };
  }, [tableId]);

  // ── Arm mode setters (T7 wires these to pencil buttons) ─────────────────────

  /** One-shot subtotal capture: next selection sets the subtotal then reverts to addend. */
  const armSubtotal = useCallback((): void => {
    setArm({ kind: "subtotal" });
  }, [setArm]);

  /** One-shot addend replace: next selection replaces `target` with the new cell. */
  const armReplaceAddend = useCallback(
    (target: CellRef): void => {
      setArm({ kind: "replaceAddend", target });
    },
    [setArm]
  );

  /** One-shot subtotal replace: next selection sets the subtotal then reverts to addend. */
  const armReplaceSubtotal = useCallback((): void => {
    setArm({ kind: "replaceSubtotal" });
  }, [setArm]);

  // ── Deferred value fetch for pre-populated refs (edit mode) ─────────────────

  useEffect(() => {
    const bt = boundTableRef.current;
    if (!bt) return;

    const allRefs = [...addends, ...(subtotal ? [subtotal] : [])];
    const missing = allRefs.filter((ref) => !valueMap.has(toCellKey(ref.row, ref.column)));
    if (!missing.length) return;

    void getTableCellValues(bt.contentControlId, missing)
      .then((results) => {
        setValueMap((prev) => {
          const next = new Map(prev);
          for (const r of results) {
            const k = toCellKey(r.row, r.column);
            if (!next.has(k)) next.set(k, r.value);
          }
          return next;
        });
      })
      .catch(() => undefined); // fallback: coords-only display, no spinner, no retry
    // `valueMap` excluded to prevent a feedback loop; best-effort one-shot fetch.
  }, [addends, subtotal, tableId]);

  const selectCellText = formatMessageDefault(CAPTION_TT_SELECT_CELL, "Select cell...");

  // ── Missing-table state (edit mode, tableId set but table deleted) ───────────

  if (isMissingTable) {
    return (
      // Same overflow-clipping defect as the composing state below; identical flexShrink: 0 fix (IDS-7361).
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          overflow: "hidden",
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        <div style={{ color: "var(--color-grey)", fontSize: "12px" }}>
          {formatMessage(CAPTION_TT_SELECT_CELLS_AND_SUBTOTAL)}
        </div>
        <SectionLabel text={formatMessage(CAPTION_TT_CELLS_TO_SUM)} />
        <div
          style={{
            border: "1px solid var(--color-light-grey, #ccc)",
            outline: "none",
            borderRadius: 4,
            padding: "6px 8px",
            minHeight: 100,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            alignContent: "flex-start",
            gap: "4px",
            opacity: 0.7,
          }}
        >
          {addends.map((a, i) => (
            <React.Fragment key={`missing-addend-${a.row}-${a.column}`}>
              {i > 0 && <span style={{ color: "var(--color-blue)", fontWeight: 600, padding: "0 2px" }}>+</span>}
              <EditableCellPill
                label={formatTableTotalsCellLabel(a, valueMap.get(toCellKey(a.row, a.column)))}
                ariaLabelBase={formatTableTotalsCellAddress(a)}
                onEdit={() => armReplaceAddend(a)}
                onRemove={() => onRemoveAddend(a)}
                maxWidth={200}
              />
            </React.Fragment>
          ))}
        </div>
        <SectionLabel text={formatMessage(CAPTION_TT_SUBTOTALS)} />
        <div>
          {subtotal && (
            <EditableCellPill
              label={formatTableTotalsCellLabel(subtotal, valueMap.get(toCellKey(subtotal.row, subtotal.column)))}
              ariaLabelBase={formatTableTotalsCellAddress(subtotal)}
              onEdit={armReplaceSubtotal}
              onRemove={() => onClearSubtotal()}
              maxWidth={200}
            />
          )}
        </div>
      </div>
    );
  }

  // ── No-table state: show searchable table list ───────────────────────────────

  if (!tableId) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", overflow: "hidden", minWidth: 0 }}>
        <div style={{ color: "var(--color-grey)", fontSize: "12px" }}>
          {formatMessage(CAPTION_TT_SELECT_TABLE_TO_START)}
        </div>
        <FilterBar
          onFilter={(query) => setSearchQuery(query)}
          currentQuery={searchQuery}
          currentFilters={[]}
          itemsFound={searchQuery ? filteredTables.length : undefined}
          styles={{
            root: {
              display: "flex",
              alignItems: "center",
              width: "100%",
              padding: "4px 0 0 0",
            },
          }}
        />
        <div
          style={{
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            paddingTop: "4px",
          }}
        >
          {filteredTables.length === 0 ? (
            <div style={{ color: "var(--color-grey)", fontSize: "12px", padding: "8px 0" }}>
              {formatMessage(CAPTION_TT_NO_TABLES)}
            </div>
          ) : (
            filteredTables.map((entry) => (
              <ObjectListItemRow
                key={entry.id}
                objectItem={entry.item}
                showObjectIcon
                buttonsAlignment="left"
                onClick={async () => {
                  await selectContentControlById(entry.contentControlId);
                }}
                hoverButtons={[
                  {
                    key: "select",
                    icon: "/assets/SelectTable.png",
                    ariaLabel: "Select table",
                    onClick: () => onBindTable(entry),
                    buttonStyle: {
                      backgroundColor: "var(--color-blue)",
                      borderRadius: "6px",
                      width: 24,
                      height: 24,
                      minWidth: 24,
                      marginLeft: 8,
                      padding: 2,
                    },
                    iconStyle: { width: 16, height: 16 },
                  },
                ]}
              />
            ))
          )}
        </div>
      </div>
    );
  }

  // ── Composing state: table bound ─────────────────────────────────────────────

  return (
    // flexShrink: 0 keeps this panel at its natural content height instead of letting the
    // AddRuleModal body flex container compress it below that height; the body's existing
    // overflowY: "auto" then scrolls the whole panel (including the Subtotals section below)
    // into view on short viewports instead of this root's `overflow: "hidden"` silently
    // clipping it (IDS-7361).
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        overflow: "hidden",
        minWidth: 0,
        flexShrink: 0,
      }}
    >
      <div style={{ color: "var(--color-grey)", fontSize: "12px" }}>
        {formatMessage(CAPTION_TT_SELECT_CELLS_AND_SUBTOTAL)}
      </div>

      {/* Cells to sum — arming is always-on via useEffect (fix 6) */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
        <div style={{ flexShrink: 0 }}>
          <SectionLabel text={formatMessage(CAPTION_TT_CELLS_TO_SUM)} />
        </div>
        {boundTable && (
          <Link
            aria-label={formatMessageDefault("cof.validation.button.gototable", "Select table")}
            onClick={async () => {
              await selectContentControlById(boundTable.contentControlId);
            }}
            style={{
              color: "var(--color-black)",
              cursor: "pointer",
              marginLeft: "auto",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              <span className="TABLE icon_class" style={{ lineHeight: 1 }} />
              <span style={{ lineHeight: 1 }}>{boundTable.name}</span>
            </div>
          </Link>
        )}
      </div>
      <div
        data-testid="cells-to-sum-box"
        style={{
          border: "1px solid var(--color-light-grey, #ccc)",
          outline: "none",
          borderRadius: 4,
          padding: "6px 8px",
          minHeight: 100,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          alignContent: "flex-start",
          gap: "4px",
          overflowY: "auto",
        }}
      >
        {addends.length === 0 ? (
          <span data-testid="cells-to-sum-placeholder" style={{ color: "var(--color-grey)", fontSize: 12 }}>
            {selectCellText}
          </span>
        ) : (
          addends.map((a, i) => {
            const isAwaiting =
              armMode.kind === "replaceAddend" && armMode.target.row === a.row && armMode.target.column === a.column;

            return (
              <React.Fragment key={`addend-${a.row}-${a.column}`}>
                {i > 0 && <span style={{ color: "var(--color-blue)", fontWeight: 600, padding: "0 2px" }}>+</span>}
                <EditableCellPill
                  label={formatTableTotalsCellLabel(a, valueMap.get(toCellKey(a.row, a.column)))}
                  ariaLabelBase={formatTableTotalsCellAddress(a)}
                  onEdit={() => armReplaceAddend(a)}
                  onRemove={() => onRemoveAddend(a)}
                  maxWidth={200}
                  awaitingText={isAwaiting ? selectCellText : undefined}
                />
              </React.Fragment>
            );
          })
        )}
      </div>

      {/* Subtotals */}
      <SectionLabel text={formatMessage(CAPTION_TT_SUBTOTALS)} />
      <div>
        {subtotal ? (
          <EditableCellPill
            label={formatTableTotalsCellLabel(subtotal, valueMap.get(toCellKey(subtotal.row, subtotal.column)))}
            ariaLabelBase={formatTableTotalsCellAddress(subtotal)}
            onEdit={armReplaceSubtotal}
            onRemove={() => onClearSubtotal()}
            maxWidth={200}
            awaitingText={armMode.kind === "replaceSubtotal" ? selectCellText : undefined}
          />
        ) : (
          <button
            type="button"
            style={{
              ...PILL_BASE_STYLE,
              backgroundColor: "white",
              color: "var(--color-blue)",
              border: "1px solid var(--color-blue)",
              cursor: "pointer",
            }}
            onClick={armSubtotal}
          >
            {armMode.kind === "subtotal" ? selectCellText : formatMessage(CAPTION_TT_SELECT_SUBTOTAL)}
          </button>
        )}
      </div>
    </div>
  );
};
